<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import OrdersApi from 'dashboard/api/orders';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const order = ref(null);
const isLoading = ref(true);

const orderId = computed(() => route.params.orderId);
const paymentInstructions = computed(
  () => order.value?.payment_method_instructions || []
);
const events = computed(() => order.value?.events || []);

async function fetchOrder() {
  try {
    isLoading.value = true;
    const response = await OrdersApi.show(orderId.value);
    order.value = response.data?.payload || response.data;
  } catch (error) {
    useAlert(t('ORDERS_PAGE.ERRORS.LOAD_FAILED'));
  } finally {
    isLoading.value = false;
  }
}

onMounted(fetchOrder);

function goBack() {
  router.push({
    name: 'orders_payments_list',
    params: { inboxId: route.params.inboxId },
  });
}

function navigateToConversation() {
  if (!order.value?.conversation_id) return;

  router.push({
    name: 'inbox_conversation',
    params: {
      conversation_id: order.value.conversation_id,
    },
  });
}

function formatCurrency(amount) {
  if (amount === null || amount === undefined) return '-';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(Number(amount) / 100);
}

function formatDate(dateStr) {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleString();
}

function statusLabel(status) {
  const labels = {
    pending: () => t('ORDERS_PAGE.STATUS.PENDING'),
    processing: () => t('ORDERS_PAGE.STATUS.PROCESSING'),
    'partially-shipped': () => t('ORDERS_PAGE.STATUS.PARTIALLY_SHIPPED'),
    shipped: () => t('ORDERS_PAGE.STATUS.SHIPPED'),
    completed: () => t('ORDERS_PAGE.STATUS.COMPLETED'),
    canceled: () => t('ORDERS_PAGE.STATUS.CANCELED'),
    captured: () => t('ORDERS_PAGE.STATUS.CAPTURED'),
    failed: () => t('ORDERS_PAGE.STATUS.FAILED'),
  };
  return (labels[status] || labels.pending)();
}

function paymentMethodLabel(method) {
  const labels = {
    pix_dynamic_code: () => t('ORDERS_PAGE.PAYMENT_METHODS.PIX_DYNAMIC_CODE'),
    boleto: () => t('ORDERS_PAGE.PAYMENT_METHODS.BOLETO'),
    payment_link: () => t('ORDERS_PAGE.PAYMENT_METHODS.PAYMENT_LINK'),
  };
  return labels[method] ? labels[method]() : method;
}

function instructionValue(instruction) {
  return (
    instruction.pix_code ||
    instruction.boleto_digitable_line ||
    instruction.payment_link_uri
  );
}

function instructionActionLabel(instruction) {
  if (instruction.method_type === 'boleto') {
    return t('ORDERS_PAGE.ACTIONS.COPY_BOLETO');
  }
  if (instruction.method_type === 'payment_link') {
    return t('ORDERS_PAGE.ACTIONS.OPEN_PAYMENT_LINK');
  }
  return t('ORDERS_PAGE.ACTIONS.COPY_PIX');
}

async function handleInstructionAction(instruction) {
  const value = instructionValue(instruction);
  if (!value) return;

  if (instruction.method_type === 'payment_link') {
    window.open(value, '_blank', 'noopener,noreferrer');
    return;
  }

  try {
    await navigator.clipboard.writeText(value);
    useAlert(t('ORDERS_PAGE.ACTIONS.COPY_SUCCESS'));
  } catch (error) {
    useAlert(t('ORDERS_PAGE.ERRORS.COPY_FAILED'));
  }
}

function methodIcon(method) {
  const icons = {
    pix_dynamic_code: 'i-lucide-qr-code',
    boleto: 'i-lucide-barcode',
    payment_link: 'i-lucide-external-link',
  };
  return icons[method] || 'i-lucide-credit-card';
}
</script>

<template>
  <section class="flex flex-col w-full h-full overflow-hidden bg-n-surface-1">
    <header class="sticky top-0 z-10 px-6 bg-n-surface-1">
      <div class="w-full max-w-6xl mx-auto">
        <div class="flex items-center justify-between w-full h-20 gap-2">
          <div class="flex items-center gap-2 min-w-0">
            <Button
              icon="i-lucide-arrow-left"
              size="sm"
              variant="ghost"
              color-scheme="secondary"
              @click="goBack"
            />
            <span class="text-heading-1 text-n-slate-12 truncate">
              {{ order?.ref_id || t('ORDERS_PAGE.DETAILS.TITLE') }}
            </span>
          </div>
          <Button
            v-if="order?.conversation_id"
            :label="t('ORDERS_PAGE.VIEW_IN_CONVERSATION')"
            icon="i-lucide-message-circle"
            size="sm"
            variant="ghost"
            color-scheme="secondary"
            @click="navigateToConversation"
          />
        </div>
      </div>
    </header>

    <main class="flex-1 px-6 overflow-y-auto">
      <div class="w-full max-w-6xl mx-auto py-4">
        <div
          v-if="isLoading"
          class="flex items-center justify-center py-10 text-n-slate-11"
        >
          <Spinner />
        </div>

        <div v-else-if="order" class="space-y-6">
          <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div class="p-4 rounded-lg bg-n-solid-2 border border-n-weak">
              <span class="block text-xs text-n-slate-10 mb-1">
                {{ t('ORDERS_PAGE.DETAILS.TOTAL') }}
              </span>
              <span class="text-lg font-semibold text-n-slate-12">
                {{ formatCurrency(order.total_amount) }}
              </span>
            </div>
            <div class="p-4 rounded-lg bg-n-solid-2 border border-n-weak">
              <span class="block text-xs text-n-slate-10 mb-1">
                {{ t('ORDERS_PAGE.FILTERS.ORDER_STATUS') }}
              </span>
              <span class="text-sm font-medium text-n-slate-12">
                {{ statusLabel(order.order_status || order.status) }}
              </span>
            </div>
            <div class="p-4 rounded-lg bg-n-solid-2 border border-n-weak">
              <span class="block text-xs text-n-slate-10 mb-1">
                {{ t('ORDERS_PAGE.FILTERS.PAYMENT_STATUS') }}
              </span>
              <span class="text-sm font-medium text-n-slate-12">
                {{ statusLabel(order.payment_status) }}
              </span>
            </div>
            <div class="p-4 rounded-lg bg-n-solid-2 border border-n-weak">
              <span class="block text-xs text-n-slate-10 mb-1">
                {{ t('ORDERS_PAGE.DETAILS.CREATED_AT') }}
              </span>
              <span class="text-sm font-medium text-n-slate-12">
                {{ formatDate(order.created_at) }}
              </span>
            </div>
          </div>

          <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <section class="space-y-3">
              <h2 class="text-sm font-semibold text-n-slate-12">
                {{ t('ORDERS_PAGE.DETAILS.ORDER_DATA') }}
              </h2>
              <dl class="divide-y divide-n-weak border-y border-n-weak">
                <div class="grid grid-cols-2 gap-4 py-3">
                  <dt class="text-xs text-n-slate-10">
                    {{ t('ORDERS_PAGE.DETAILS.CUSTOMER') }}
                  </dt>
                  <dd class="text-sm text-n-slate-12">
                    {{
                      order.contact_name || t('ORDERS_PAGE.UNKNOWN_CUSTOMER')
                    }}
                  </dd>
                </div>
                <div class="grid grid-cols-2 gap-4 py-3">
                  <dt class="text-xs text-n-slate-10">
                    {{ t('ORDERS_PAGE.DETAILS.PROVIDER') }}
                  </dt>
                  <dd class="text-sm text-n-slate-12">
                    {{ order.provider_name || '-' }}
                  </dd>
                </div>
                <div class="grid grid-cols-2 gap-4 py-3">
                  <dt class="text-xs text-n-slate-10">
                    {{ t('ORDERS_PAGE.DETAILS.PROVIDER_ORDER_ID') }}
                  </dt>
                  <dd class="text-sm text-n-slate-12">
                    {{ order.provider_order_id || '-' }}
                  </dd>
                </div>
                <div class="grid grid-cols-2 gap-4 py-3">
                  <dt class="text-xs text-n-slate-10">
                    {{ t('ORDERS_PAGE.DETAILS.UPDATED_AT') }}
                  </dt>
                  <dd class="text-sm text-n-slate-12">
                    {{ formatDate(order.updated_at) }}
                  </dd>
                </div>
              </dl>

              <div
                v-if="order.has_error"
                class="p-3 rounded-lg border border-n-ruby-6 bg-n-ruby-3 text-sm text-n-ruby-11"
              >
                <div class="font-medium">
                  {{
                    order.last_meta_error_code || t('ORDERS_PAGE.DETAILS.ERROR')
                  }}
                </div>
                <div>{{ order.last_meta_error_message }}</div>
              </div>
            </section>

            <section class="space-y-3">
              <h2 class="text-sm font-semibold text-n-slate-12">
                {{ t('ORDERS_PAGE.DETAILS.PAYMENT_INSTRUCTIONS') }}
              </h2>
              <div
                v-if="paymentInstructions.length"
                class="divide-y divide-n-weak border-y border-n-weak"
              >
                <div
                  v-for="instruction in paymentInstructions"
                  :key="instruction.id"
                  class="flex items-center justify-between gap-3 py-3"
                >
                  <div class="flex items-center gap-3 min-w-0">
                    <span
                      :class="methodIcon(instruction.method_type)"
                      class="size-5 text-n-slate-10"
                    />
                    <div class="min-w-0">
                      <div class="text-sm font-medium text-n-slate-12">
                        {{ paymentMethodLabel(instruction.method_type) }}
                      </div>
                      <div class="text-xs text-n-slate-10 truncate">
                        {{ instructionValue(instruction) || '-' }}
                      </div>
                    </div>
                  </div>
                  <Button
                    v-if="instructionValue(instruction)"
                    :label="instructionActionLabel(instruction)"
                    icon="i-lucide-copy"
                    size="xs"
                    variant="ghost"
                    color-scheme="secondary"
                    @click="handleInstructionAction(instruction)"
                  />
                </div>
              </div>
              <div v-else class="text-sm text-n-slate-10">
                {{ t('ORDERS_PAGE.DETAILS.NO_PAYMENT_INSTRUCTIONS') }}
              </div>
            </section>
          </div>

          <section class="space-y-3">
            <h2 class="text-sm font-semibold text-n-slate-12">
              {{ t('ORDERS_PAGE.DETAILS.EVENTS') }}
            </h2>
            <div
              v-if="events.length"
              class="divide-y divide-n-weak border-y border-n-weak"
            >
              <div
                v-for="event in events"
                :key="event.id"
                class="grid grid-cols-1 md:grid-cols-[10rem_1fr_12rem] gap-3 py-3 text-sm"
              >
                <div class="text-n-slate-12 font-medium">
                  {{ event.event_type }}
                </div>
                <div class="text-n-slate-10">
                  {{ event.source }} {{ t('ORDERS_PAGE.SEPARATOR') }}
                  {{ event.processing_status }}
                  <span v-if="event.error_message">
                    {{ t('ORDERS_PAGE.SEPARATOR') }}
                    {{ event.error_message }}
                  </span>
                </div>
                <div class="text-n-slate-10 md:text-right">
                  {{ formatDate(event.processed_at || event.created_at) }}
                </div>
              </div>
            </div>
            <div v-else class="text-sm text-n-slate-10">
              {{ t('ORDERS_PAGE.DETAILS.NO_EVENTS') }}
            </div>
          </section>

          <section class="space-y-3">
            <h2 class="text-sm font-semibold text-n-slate-12">
              {{ t('ORDERS_PAGE.DETAILS.RAW_PAYLOAD') }}
            </h2>
            <pre
              class="max-h-80 overflow-auto rounded-lg bg-n-solid-2 border border-n-weak p-4 text-xs text-n-slate-11"
            >
              {{ JSON.stringify(order.raw_order_payload || {}, null, 2) }}
            </pre>
          </section>
        </div>
      </div>
    </main>
  </section>
</template>
